/**
 * Timezone Helper
 *
 * Utility functions for timezone calculations and formatting
 * Ported from old QMC dashboard-scheduled-orders-update.component.ts (lines 549-603)
 *
 * IMPORTANT: These methods match the old QMC business logic exactly
 */
export class TimezoneHelper {
  /**
   * Get customer's next occurrence in their timezone
   * Matches old QMC lines 549-568
   *
   * @param date Next occurrence date
   * @param time Time string in HH:mm format
   * @param utcOffset UTC offset in hours (e.g., -5 for EST, +1 for CET)
   * @returns Date object in customer's timezone
   */
  static getCustomerNextOccurrence(
    date: Date,
    time: string,
    utcOffset: number | null,
  ): Date | undefined {
    if (!date || !time) {
      return undefined;
    }

    // Format timezone offset as +HH:00 or -HH:00
    let timezone: string = Math.abs(utcOffset || 0)
      .toString()
      .padStart(2, "0");

    if ((utcOffset || 0) >= 0) {
      timezone = "+" + timezone + ":00";
    } else {
      timezone = "-" + timezone + ":00";
    }

    // Build ISO 8601 date string with timezone
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");

    const isoString = `${year}-${month}-${day}T${time}:00.000${timezone}`;
    const timestamp = Date.parse(isoString);
    const nextOccurrence = new Date(timestamp);

    return nextOccurrence;
  }

  /**
   * Get processing time with next occurrence offset applied
   * Matches old QMC lines 570-590
   *
   * @param date Next occurrence date
   * @param time Time string in HH:mm format
   * @param utcOffset UTC offset in hours
   * @param nextOccurrenceOffset Number of days before next occurrence to process
   * @returns Date object for when processing should occur
   */
  static getProcessingTime(
    date: Date,
    time: string,
    utcOffset: number | null,
    nextOccurrenceOffset: number,
  ): Date | undefined {
    if (!date || !time || !nextOccurrenceOffset) {
      return undefined;
    }

    // Shift date back by nextOccurrenceOffset days
    const d = new Date(date);
    const shiftedDate = new Date(d.setDate(d.getDate() - nextOccurrenceOffset));

    // Format timezone offset as +HH:00 or -HH:00
    let timezone: string = Math.abs(utcOffset || 0)
      .toString()
      .padStart(2, "0");

    if ((utcOffset || 0) >= 0) {
      timezone = "+" + timezone + ":00";
    } else {
      timezone = "-" + timezone + ":00";
    }

    // Build ISO 8601 date string with timezone
    const year = shiftedDate.getFullYear();
    const month = (shiftedDate.getMonth() + 1).toString().padStart(2, "0");
    const day = shiftedDate.getDate().toString().padStart(2, "0");

    const isoString = `${year}-${month}-${day}T${time}:00.000${timezone}`;
    const processingTime = new Date(Date.parse(isoString));

    return processingTime;
  }

  /**
   * Format UTC offset for display
   * Matches old QMC lines 592-603
   *
   * @param utcOffset UTC offset in hours
   * @returns Formatted string like "UTC+05" or "UTC-08"
   */
  static getFormattedUtcOffset(utcOffset: number | null): string {
    if (!utcOffset) {
      return "UTC";
    }

    let timezone: string = Math.abs(utcOffset).toString().padStart(2, "0");

    if (utcOffset >= 0) {
      timezone = `UTC+${timezone}`;
    } else {
      timezone = `UTC-${timezone}`;
    }

    return timezone;
  }

  /**
   * Parse time string to hours and minutes
   *
   * @param time Time string in HH:mm format
   * @returns Object with hours and minutes, or null if invalid
   */
  static parseTime(
    time: string,
  ): { hours: number; minutes: number } | undefined {
    if (!time) {
      return undefined;
    }

    const parts = time.split(":");
    if (parts.length !== 2) {
      return undefined;
    }

    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);

    if (isNaN(hours) || isNaN(minutes)) {
      return undefined;
    }

    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
      return undefined;
    }

    return { hours, minutes };
  }

  /**
   * Format time object to HH:mm string
   *
   * @param hours Hours (0-23)
   * @param minutes Minutes (0-59)
   * @returns Time string in HH:mm format
   */
  static formatTime(hours: number, minutes: number): string {
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
  }

  /**
   * Get list of timezone offsets
   * Returns common timezone offsets from UTC-12 to UTC+14
   *
   * @returns Array of timezone offset objects with label and value
   */
  static getTimezoneOffsets(): { label: string; value: number }[] {
    const offsets = [];

    for (let offset = -12; offset <= 14; offset++) {
      offsets.push({
        label: TimezoneHelper.getFormattedUtcOffset(offset),
        value: offset,
      });
    }

    return offsets;
  }
}
